import React, { useState, useContext, useEffect, useCallback } from 'react';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { datadogRum } from '@datadog/browser-rum';
import { NavigateToWindowWithNativeEvents } from '@components/_common/link/link';
import { NativeMessageEvent, sendNativeMessage } from '@carvana/core-native-messaging';
import {
  PrePurchaseModal,
  BodyHeader,
  BodyText,
  CancellationSuccess,
  IconContainer,
  CancellationFailure,
  ButtonContainer,
  CustomPrimaryButton,
  CustomTertiaryButton,
  CancellationPrompt,
} from './CancelPrePurchase.styles';
// import DuoCheckIcon from './DuoCheckIcon';
import { DefaultSpinner } from '../spinner/Spinner';
import DuoCheckIconGreen from '../../../assets/DuoCheckIconGreen';
import ErrorPageIcon from '../../../assets/ErrorPageIcon';
import { deletePrePurchase } from '../../../store/GaragePageStore';
import CancelCautionIcon from '../../../assets/CancelCautionIcon';
import { AnalyticEvents, AppRoute, CancellationReason } from '../../../constants';

const CancelPrePurchaseModal = ({ isOpen, closeModal, vehicleDetails }: any) => {
  const { trackEvent } = useContext(AnalyticsContext);
  const [isCancellationSuccess, setCancellationSuccess] = useState(false);

  const [isCancellationFailure, setCancellationFailure] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { vehicleId, vehicleState } = vehicleDetails; // TODO: insert vehicle id and verify the inventory type

  const eventPayload = {
    vehicleId,
    vehicleState,
  };

  // Modal Window close handler
  const handleCloseModal = () => {
    closeModal();
    setCancellationSuccess(false);
    setCancellationFailure(false);

    setIsLoading(false);
    if (!isCancellationSuccess) {
      trackEvent(AnalyticEvents.cancelOrderDismissed, eventPayload);
      datadogRum.addAction(AnalyticEvents.cancelOrderDismissed, eventPayload);
    } else {
      NavigateToWindowWithNativeEvents({
        url: AppRoute.MY_CARVANA,
        event: 'continue_to_my_cars',
      });
    }
  };

  const keyboardHandler = useCallback(
    // close modal if 'esc' key pressed
    (event: any) => {
      // event.keyCode === 27  is the "Esc" key
      if (event.keyCode === 27 && isOpen) {
        handleCloseModal();
      }
    },
    [isOpen]
  );

  const cancelPrePurchase = async () => {
    const env = process.env.NODE_ENV;
    const cancellationPayload = {
      notes: '',
      reasons: [CancellationReason.CUSTOMER_SELF_CANCELLED],
      executeReturnVehicle: true,
    };
    trackEvent(AnalyticEvents.cancelOrderConfirmed, eventPayload);
    datadogRum.addAction(AnalyticEvents.cancelOrderConfirmed, eventPayload);
    try {
      const result = await deletePrePurchase(cancellationPayload, env);
      if (result === true) {
        setCancellationSuccess(result);
        trackEvent(AnalyticEvents.orderCancelled, eventPayload);
        datadogRum.addAction(AnalyticEvents.orderCancelled, eventPayload);
        sendNativeMessage('purchase_cancelled' as NativeMessageEvent, {}, 'mycars');
      }
      if (result.errorMessage) {
        setCancellationFailure(true);
      }
    } catch (error) {
      console.error(error);
      setCancellationFailure(true);
    }
  };

  useEffect(() => {
    // listeners - close modal if 'esc' key pressed
    document.addEventListener('keydown', keyboardHandler);
    return () => {
      document.removeEventListener('keydown', keyboardHandler);
    };
  }, [keyboardHandler]);

  /** Close and redirect user to /cars on closing the modal
   * after successful cancellation
   * */
  const handleCancelSuccessContinueShopping = async () => {
    trackEvent(AnalyticEvents.continueAfterCancellationClicked, eventPayload);
    datadogRum.addAction(AnalyticEvents.continueAfterCancellationClicked, eventPayload);
    await closeModal();
    NavigateToWindowWithNativeEvents({
      url: AppRoute.CARS,
      event: 'continue_to_srp',
      payload: {
        source: 'my_cars',
      },
    });
  };

  const showCancellationPrompt = !isLoading && !isCancellationSuccess && !isCancellationFailure;
  const showCancellationFailure = !isCancellationSuccess && isCancellationFailure;

  // const initialValues = {
  //   cancellationText: '',
  // };

  const loaderStyles = {
    minHeight: '338px',
    marginTop: '20%',
  };

  return (
    <PrePurchaseModal headerText="Cancel Order" closeModal={handleCloseModal} isOpen={isOpen}>
      {isLoading && <DefaultSpinner customStyles={loaderStyles} />}
      {showCancellationPrompt && (
        <CancellationPrompt>
          <IconContainer marginTop="55px" marginBottom="46px">
            <CancelCautionIcon />
          </IconContainer>
          <BodyHeader data-testid="cancel-purchase-header-text">Are you sure you want to cancel your order?</BodyHeader>
          <BodyText data-testid="cancel-purchase-body-text" maxWidth={434}>
            Your vehicle will be immediately available for other customers. This action cannot be undone.
          </BodyText>
          <ButtonContainer marginRight="20px">
            <CustomPrimaryButton data-testid="cancel-purchase-confirm-button" onClick={cancelPrePurchase}>
              YES, CANCEL
            </CustomPrimaryButton>
            <CustomTertiaryButton data-testid="cancel-purchase-cancel-button" onClick={handleCloseModal}>
              NO, GO BACK
            </CustomTertiaryButton>
          </ButtonContainer>
        </CancellationPrompt>
      )}

      {/* Pre-purchase Cancellation Success */}
      {isCancellationSuccess && (
        <CancellationSuccess>
          <IconContainer marginTop="59px" marginBottom="38px">
            <DuoCheckIconGreen />
          </IconContainer>
          <BodyHeader data-testid="cancel-purchase-success-header-text">Your order has been canceled.</BodyHeader>
          <BodyText data-testid="cancel-purchase-success-body-text" maxWidth={460}>
            We’re sorry this one didn’t work out. If you’re still interested in a vehicle, check out our inventory.
            There’s plenty of rides to love!
          </BodyText>
          <ButtonContainer marginRight={0}>
            <CustomPrimaryButton
              data-testid="cancel-purchase-success-continue-button"
              onClick={handleCancelSuccessContinueShopping}
            >
              Continue Shopping
            </CustomPrimaryButton>
          </ButtonContainer>
        </CancellationSuccess>
      )}

      {/* Pre-purchase Cancellation Failure */}
      {showCancellationFailure && (
        <CancellationFailure>
          <IconContainer marginTop="45px" marginBottom="27px">
            <ErrorPageIcon />
          </IconContainer>
          <BodyHeader data-testid="cancel-purchase-error-header-text">Oops, something weird happened here.</BodyHeader>
          <BodyText data-testid="cancel-purchase-error-body-text" maxWidth={390}>
            We’re having trouble processing your order cancellation. Please try again later.
          </BodyText>
          <ButtonContainer marginRight={0}>
            <CustomPrimaryButton data-testid="cancel-purchase-error-continue-button" onClick={handleCloseModal}>
              Continue
            </CustomPrimaryButton>
          </ButtonContainer>
        </CancellationFailure>
      )}
    </PrePurchaseModal>
  );
};

export default CancelPrePurchaseModal;
